/**
 * The Compare rail's multi-select bar: tick rows, then ★ them all in one go.
 *
 * Shown only while select mode is on (A/B view only — the starred-list view is
 * read-only). Single-★ clicking on a row is untouched; this is the "select a
 * few, star them together" path.
 */

interface Props {
  /** How many rail rows are ticked. */
  selectedCount: number;
  /** How many rows are visible to "Select all" (respects grouping/collapse). */
  visibleCount: number;
  /** True when every visible row is already ticked (Select all ⇆ Clear). */
  allSelected: boolean;
  /** Ticked rows that are NOT yet starred — drives the ★ button's count. */
  unstarredCount: number;
  /** Ticked rows that ARE starred — drives the un-★ button. */
  starredCount: number;
  onSelectAll: () => void;
  onClear: () => void;
  onStarSelected: () => void;
  onUnstarSelected: () => void;
  onExit: () => void;
  /** Result line from the last bulk action. */
  message?: string | null;
  /** Standing warning while the selection sits at its cap. */
  capNote?: string | null;
}

export default function RailBulkStarBar({
  selectedCount,
  visibleCount,
  allSelected,
  unstarredCount,
  starredCount,
  onSelectAll,
  onClear,
  onStarSelected,
  onUnstarSelected,
  onExit,
  message,
  capNote,
}: Props) {
  return (
    <div
      data-testid="rail-bulk-bar"
      className="sticky top-0 z-10 mb-2 rounded border border-line bg-panel p-2"
    >
      <div className="flex items-center justify-between gap-1">
        <span className="text-[11px] font-semibold text-ink" data-testid="rail-bulk-count">
          {selectedCount} selected
        </span>
        <button
          onClick={onExit}
          title="Leave select mode"
          className="rounded px-1 text-[11px] text-ink-3 hover:bg-line-subtle"
        >
          Done
        </button>
      </div>

      <div className="mt-1.5 flex flex-wrap gap-1">
        <button
          onClick={onStarSelected}
          disabled={unstarredCount === 0}
          data-testid="rail-bulk-star"
          title="Star every selected drug at once"
          className="inline-flex items-center gap-1 rounded border border-line-input bg-white px-2 py-1 text-[11px] font-semibold text-[#C98A1B] hover:bg-panel disabled:cursor-not-allowed disabled:border-line disabled:text-ink-3 disabled:hover:bg-white"
        >
          <span>★</span>
          Star selected
          {unstarredCount > 0 && (
            <span className="rounded-full bg-panel px-1.5 tabular-nums">{unstarredCount}</span>
          )}
        </button>
        <button
          onClick={onUnstarSelected}
          disabled={starredCount === 0}
          data-testid="rail-bulk-unstar"
          title="Remove every selected drug from the shortlist"
          className="inline-flex items-center gap-1 rounded border border-line bg-white px-2 py-1 text-[11px] font-medium text-ink-2 hover:bg-panel disabled:cursor-not-allowed disabled:text-ink-3 disabled:hover:bg-white"
        >
          <span>☆</span>
          Unstar
          {starredCount > 0 && (
            <span className="rounded-full bg-panel px-1.5 tabular-nums">{starredCount}</span>
          )}
        </button>
        <button
          onClick={allSelected ? onClear : onSelectAll}
          disabled={visibleCount === 0}
          data-testid="rail-bulk-selectall"
          className="rounded border border-line bg-white px-2 py-1 text-[11px] font-medium text-ink-2 hover:bg-panel disabled:cursor-not-allowed disabled:text-ink-3"
        >
          {allSelected ? 'Clear' : `Select all (${visibleCount})`}
        </button>
      </div>

      {message && (
        <div className="mt-1.5 text-[10px] leading-tight text-ink-2" data-testid="rail-bulk-message">
          {message}
        </div>
      )}
      {capNote && (
        <div className="mt-1.5 rounded border border-warn-line bg-warn-bg px-1.5 py-1 text-[10px] font-semibold leading-tight text-warn-ink" data-testid="rail-bulk-capnote">
          {capNote}
        </div>
      )}
      <div className="mt-1 text-[10px] leading-tight text-ink-3">
        Tick the rows you want, then <b>Star selected</b>.
      </div>
    </div>
  );
}
